import { Column, Funnel, Line, Pie } from '@ant-design/charts';
import { tiyinToSom } from '@/shared/lib/format';

const HEIGHT = 280;

/** Compact soʻm label for chart axes/tooltips. */
function somLabel(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(1)} mlrd`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)} mln`;
  if (abs >= 1e3) return `${Math.round(v / 1e3)} ming`;
  return String(v);
}
const moneyAxis = { y: { labelFormatter: (v: number) => somLabel(Number(v)) } };
const moneyTooltip = (name: string) => ({
  items: [{ channel: 'y' as const, name, valueFormatter: (v: number) => `${somLabel(Number(v))} so‘m` }],
});
/** Formats only the tooltip value as soʻm, preserving the series name. */
const moneyValueTooltip = {
  items: [{ channel: 'y' as const, valueFormatter: (v: number) => `${somLabel(Number(v))} so‘m` }],
};

/** Revenue trend: contracted vs collected per month (money in soʻm). */
export function RevenueTrendChart({
  buckets,
  labels,
}: {
  buckets: Array<{ month: string; contracted: string; collected: string }>;
  labels: { contracted: string; collected: string };
}) {
  const data = buckets.flatMap((b) => [
    { month: b.month, type: labels.contracted, value: tiyinToSom(b.contracted) },
    { month: b.month, type: labels.collected, value: tiyinToSom(b.collected) },
  ]);
  return (
    <Line
      data={data}
      height={HEIGHT}
      xField="month"
      yField="value"
      colorField="type"
      axis={moneyAxis}
      tooltip={moneyValueTooltip}
      scale={{ color: { range: ['#2563eb', '#16a34a'] } }}
    />
  );
}

/** Collections: expected vs collected per month (grouped columns). */
export function CollectionsChart({
  buckets,
  labels,
}: {
  buckets: Array<{ month: string; expected: string; collected: string }>;
  labels: { expected: string; collected: string };
}) {
  const data = buckets.flatMap((b) => [
    { month: b.month, type: labels.expected, value: tiyinToSom(b.expected) },
    { month: b.month, type: labels.collected, value: tiyinToSom(b.collected) },
  ]);
  return (
    <Column
      data={data}
      height={HEIGHT}
      xField="month"
      yField="value"
      colorField="type"
      group
      axis={moneyAxis}
      tooltip={moneyValueTooltip}
      scale={{ color: { range: ['#94a3b8', '#16a34a'] } }}
    />
  );
}

/** Cash-flow categories: income (green) vs expense (red) as horizontal bars. */
export function CashFlowChart({
  income,
  expense,
  labels,
}: {
  income: Array<{ category: string; amount: string }>;
  expense: Array<{ category: string; amount: string }>;
  labels: { income: string; expense: string };
}) {
  const data = [
    ...income.map((i) => ({ category: i.category, value: tiyinToSom(i.amount), type: labels.income })),
    ...expense.map((e) => ({ category: e.category, value: tiyinToSom(e.amount), type: labels.expense })),
  ];
  return (
    <Column
      data={data}
      height={HEIGHT}
      xField="category"
      yField="value"
      colorField="type"
      group
      axis={moneyAxis}
      scale={{ color: { range: ['#16a34a', '#dc2626'] } }}
      tooltip={moneyTooltip('')}
    />
  );
}

/** Sales funnel stages. */
export function SalesFunnelChart({ stages }: { stages: Array<{ name: string; count: number }> }) {
  return <Funnel data={stages} height={HEIGHT} xField="name" yField="count" />;
}

/** Inventory by status — donut. */
export function InventoryPie({
  byStatus,
  countLabel,
}: {
  byStatus: Array<{ status: string; count: number }>;
  countLabel: string;
}) {
  return (
    <Pie
      data={byStatus}
      height={HEIGHT}
      angleField="count"
      colorField="status"
      innerRadius={0.5}
      label={{ text: (d: { status: string; count: number }) => `${d.status}: ${d.count}`, position: 'outside' }}
      legend={{ color: { position: 'bottom' } }}
      tooltip={{ title: (d: { status: string }) => d.status, items: [{ field: 'count', name: countLabel }] }}
    />
  );
}
